import { useMemo } from "react";

import { isFuture, isWithinInterval } from "date-fns";
import { ETaxonomy } from "library/types";
import { formatNumber } from "library/utils";
import { useFilterCategoryOptions } from "pages/BenefitsPage/components/BenefitsPageHeader/FilterContent/CategoryFilter/useFilterCategoryOptions";

import { useBenefitsData } from "hooks/useBenefitsData";
import { useTaxonomyOptions } from "hooks/useTaxonomyOptions";

enum PaymentEntity {
  Company = "paidByCompany",
  Employee = "paidByEmployee",
  Dependent = "paidByDependent",
  CompanyForDependent = "paidByCompanyForDependent",
}

export function useBenefitPaymentsData() {
  const { packageData } = useBenefitsData();
  useFilterCategoryOptions();
  const enrolledData = packageData?.enrolledPackages || [];

  const currencyOptions = useTaxonomyOptions(ETaxonomy.Currency);

  const onlyActiveEnrollments = enrolledData.filter((benefitPackage) => {
    const activePackage = isWithinInterval(new Date(), {
      start: new Date(benefitPackage?.validFrom || ""),
      end: new Date(benefitPackage?.validTo || ""),
    });
    return activePackage;
  });

  const payments = useMemo(() => {
    if (!onlyActiveEnrollments.length) return null;
    const result = {};

    onlyActiveEnrollments.forEach((benefitPackage) => {
      const isPercentageValue = benefitPackage?.usingPercentagesForPaidBy;
      const currency = currencyOptions?.find(
        (item) => item.value === benefitPackage?.currencyCode
      )?.codeShort;

      if (benefitPackage?.dateOfCancellation) {
        if (!isFuture(new Date(benefitPackage?.dateOfCancellation))) {
          return;
        }
      } //if benefit cancellation date is in future use it in calculation
      if (!result[currency]) {
        result[currency] = {
          paidByCompany: 0,
          paidByEmployee: 0,
          paidByDependent: 0,
          paidByCompanyForDependent: 0,
          currency,
        };
      }

      if (isPercentageValue) {
        if (benefitPackage?.isDependent) {
          result[currency].paidByCompanyForDependent +=
            ((benefitPackage?.price || 0) / 100) * (benefitPackage?.paidByCompany || 0);

          result[currency].paidByDependent +=
            ((benefitPackage?.price || 0) / 100) * (benefitPackage?.paidByEmployee || 0);
        } else {
          result[currency].paidByCompany +=
            ((benefitPackage?.price || 0) / 100) * (benefitPackage?.paidByCompany || 0);

          result[currency].paidByEmployee +=
            ((benefitPackage?.price || 0) / 100) * (benefitPackage?.paidByEmployee || 0);
        }
      } else {
        if (benefitPackage?.isDependent) {
          result[currency].paidByCompanyForDependent += benefitPackage?.paidByCompany || 0;
          result[currency].paidByDependent += benefitPackage?.paidByEmployee || 0;
        } else {
          result[currency].paidByCompany += benefitPackage?.paidByCompany || 0;
          result[currency].paidByEmployee += benefitPackage?.paidByEmployee || 0;
        }
      }
    });

    return result;
  }, [enrolledData, currencyOptions, onlyActiveEnrollments]);

  const getPaymentByEntity = (valueName) => {
    // return `${Object.keys(payments || {}).map(
    //   (key) => ` ${formatNumber(payments?.[key][valueName])} ${payments?.[key]?.currency}`
    // )}`;
    const output = {};
    Object.keys(payments || {}).forEach((key) => {
      const currency = payments?.[key]?.currency;
      output[currency] = {
        label: ` ${formatNumber(payments?.[key][valueName])} ${payments?.[key]?.currency}`,
        value: payments?.[key][valueName],
        currency,
      };
    });
    return output;
  };

  const paymentsPerCategory = useMemo(() => {
    if (!onlyActiveEnrollments.length) return null;
    const result = {};

    onlyActiveEnrollments.forEach((benefitPackage) => {
      const isPercentageValue = benefitPackage?.usingPercentagesForPaidBy;
      const currency = currencyOptions?.find(
        (item) => item.value === benefitPackage?.currencyCode
      )?.codeShort;

      if (benefitPackage?.dateOfCancellation) {
        if (!isFuture(new Date(benefitPackage?.dateOfCancellation))) {
          return;
        }
      }

      const categoryTag = benefitPackage?.benefitCategoryTag;
      const categoryName = benefitPackage?.benefitCategoryName;

      if (!currency || !categoryTag) return;

      if (!result[currency]) {
        result[currency] = {};
      }

      if (!result[currency][categoryTag]) {
        result[currency][categoryTag] = {
          categoryName,
          paidByCompany: 0,
          paidByEmployee: 0,
          paidByDependent: 0,
          paidByCompanyForDependent: 0,
        };
      }

      const entry = result[currency][categoryTag];

      const price = benefitPackage?.price || 0;
      const paidByCompany = benefitPackage?.paidByCompany || 0;
      const paidByEmployee = benefitPackage?.paidByEmployee || 0;

      if (isPercentageValue) {
        if (benefitPackage?.isDependent) {
          entry.paidByCompanyForDependent += (price / 100) * paidByCompany;
          entry.paidByDependent += (price / 100) * paidByEmployee;
        } else {
          entry.paidByCompany += (price / 100) * paidByCompany;
          entry.paidByEmployee += (price / 100) * paidByEmployee;
        }
      } else {
        if (benefitPackage?.isDependent) {
          entry.paidByCompanyForDependent += paidByCompany;
          entry.paidByDependent += paidByEmployee;
        } else {
          entry.paidByCompany += paidByCompany;
          entry.paidByEmployee += paidByEmployee;
        }
      }
    });

    return result;
  }, [enrolledData, currencyOptions, onlyActiveEnrollments]);

  return {
    paidByCompany: getPaymentByEntity(PaymentEntity.Company),
    paidByEmployee: getPaymentByEntity(PaymentEntity.Employee),
    paidByCompanyForDependent: getPaymentByEntity(PaymentEntity.CompanyForDependent),
    paidByDependent: getPaymentByEntity(PaymentEntity.Dependent),
    rawPayments: payments,
    paymentsPerCategory,
  };
}
